import { Grid, SelectChangeEvent } from '@mui/material';
import { TextField } from '@mui/material';
import { FormControl } from '@mui/material';
import { InputLabel } from '@mui/material';
import { Select } from '@mui/material';
import { MenuItem } from '@mui/material';
import { Checkbox } from '@mui/material';
import { ListItemText } from '@mui/material';
import { Box } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { PortfolioValidation, User } from 'lib/types/models';
import React from 'react';

import OperationAssociation from '../../OperationAssociation';
import { OperationAssociationValue } from '../../OperationAssociation/operations';
import DocumentsDropzone, { PendingDocument } from './DocumentsDropzone';

interface Props {
  businessIdentifier?: string;
  validations: PortfolioValidation[];
  ownUsers: User[];
  businessUsers: User[];
  selectedOwnUserIds: number[];
  setSelectedOwnUserIds: (value: number[]) => void;
  businessUserIds: number[];
  setBusinessUserIds: (value: number[]) => void;
  selectedValidations: number[];
  setSelectedValidations: (value: number[]) => void;
  title: string;
  setTitle: (value: string) => void;
  validUntil: Dayjs | null;
  setValidUntil: (value: Dayjs | null) => void;
  operation: OperationAssociationValue;
  setOperation: (value: OperationAssociationValue) => void;
  documents: PendingDocument[];
  setDocuments: (value: PendingDocument[]) => void;
}

const Custom = ({
  businessIdentifier,
  validations,
  ownUsers,
  businessUsers,
  selectedOwnUserIds,
  setSelectedOwnUserIds,
  businessUserIds,
  setBusinessUserIds,
  selectedValidations,
  setSelectedValidations,
  title,
  setTitle,
  validUntil,
  setValidUntil,
  operation,
  setOperation,
  documents,
  setDocuments,
}: Props) => {
  const getUserLabel = (u: User) => {
    const displayName = u.name ?? u.email;
    return `${displayName}${u.name ? ` (${u.email})` : ''}`;
  };

  const handleOwnUsersChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value as number[];
    setSelectedOwnUserIds(value);
  };

  const handleBusinessUsersChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value as number[];
    setBusinessUserIds(value);
  };

  const handleValidationsChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value as number[];
    setSelectedValidations(value);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <TextField
          fullWidth
          margin="dense"
          label="Título del portafolio"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Ingrese el título del portafolio"
        />

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Válido hasta"
            value={validUntil}
            onChange={(newValue) => setValidUntil(newValue)}
            minDate={dayjs().startOf('day')}
            slotProps={{
              textField: {
                fullWidth: true,
                margin: 'dense',
                placeholder: 'Seleccione la fecha de vencimiento',
              },
            }}
          />
        </LocalizationProvider>

        <FormControl fullWidth margin="dense">
          <InputLabel id="own-signers-label">Tus firmantes</InputLabel>
          <Select
            labelId="own-signers-label"
            id="own-signers-select"
            multiple
            value={selectedOwnUserIds}
            label="Tus firmantes"
            onChange={handleOwnUsersChange}
            renderValue={(selected) =>
              selected
                .map((id) => ownUsers.find((u) => u.id === id))
                .filter((u): u is User => Boolean(u))
                .map((u) => getUserLabel(u))
                .join(', ')
            }
          >
            {ownUsers.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                <Checkbox checked={selectedOwnUserIds.indexOf(u.id) > -1} />
                <ListItemText primary={getUserLabel(u)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="dense">
          <InputLabel id="business-signers-label">
            Firmantes del cliente
          </InputLabel>
          <Select
            labelId="business-signers-label"
            id="business-signers-select"
            multiple
            value={businessUserIds}
            label="Firmantes del cliente"
            onChange={handleBusinessUsersChange}
            renderValue={(selected) =>
              selected
                .map((id) => businessUsers.find((u) => u.id === id))
                .filter((u): u is User => Boolean(u))
                .map((u) => getUserLabel(u))
                .join(', ')
            }
          >
            {businessUsers.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                <Checkbox checked={businessUserIds.indexOf(u.id) > -1} />
                <ListItemText primary={getUserLabel(u)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="dense">
          <InputLabel id="portfolio-validations-label">
            Validaciones del portafolio
          </InputLabel>
          <Select
            labelId="portfolio-validations-label"
            id="portfolio-validations-select"
            multiple
            value={selectedValidations}
            label="Validaciones del portafolio"
            onChange={handleValidationsChange}
            renderValue={(selected) =>
              selected
                .map((t) => validations.find((v) => v.id === t))
                .filter((v): v is PortfolioValidation => Boolean(v))
                .map((v) => v.description)
                .join(', ')
            }
          >
            {validations.map((v) => (
              <MenuItem key={v.id} value={v.id}>
                <Checkbox checked={selectedValidations.indexOf(v.id) > -1} />
                <ListItemText primary={v.description ?? ''} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box mt={1}>
          <OperationAssociation
            value={operation}
            onChange={setOperation}
            businessIdentifier={businessIdentifier}
          />
        </Box>
      </Grid>
      <Grid item xs={6}>
        <DocumentsDropzone documents={documents} onChange={setDocuments} />
      </Grid>
    </Grid>
  );
};

export default Custom;
